import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  FaArrowLeft, FaMapMarkerAlt, FaCreditCard, FaUser, 
  FaLock, FaSpinner, FaCheckCircle, FaMoneyBillWave, FaShieldAlt, 
  FaTag, FaTimes 
} from "react-icons/fa";
import Swal from 'sweetalert2';
import SummaryApi from "../common";

/**
 * REUSABLE COMPONENT: INPUT GROUP
 * Defined OUTSIDE the main component to prevent re-mounting on state changes.
 */
const InputGroup = ({ label, name, type = "text", value, onChange, placeholder, required = false, className = "", readOnly = false }) => (
  <div className={className}>
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input 
      type={type} 
      name={name}
      value={value} 
      onChange={onChange}
      readOnly={readOnly}
      className={`w-full border border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 block p-3 transition-all outline-none 
      ${readOnly ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'bg-white'}`}
      placeholder={placeholder}
    />
  </div>
);

const Checkout = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cartItems, setCartItems] = useState([]);
  
  // --- COUPON STATE ---
  const [couponCode, setCouponCode] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [isCouponApplied, setIsCouponApplied] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_mobile: "",
    email: "",
    flat_number: "",
    street_name: "",
    land_mark: "",
    city: "",
    state: "",
    pincode: "",
    paymentMethod: "cod"
  });

  const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("user_id");

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  // --- INITIALIZATION ---
  useEffect(() => {
    if (!customerId) {
      navigate('/login');
      return;
    }

    const initCheckout = async () => {
      try {
        await Promise.all([
          fetchCartData(), 
          fetchCustomerDetails(),
          fetchUserAddress()
        ]);
      } catch (error) {
        console.error("Error initializing checkout:", error);
      } finally {
        setLoading(false);
      }
    };

    initCheckout();
  }, [customerId, navigate]);

  // --- API HELPERS ---
  const fetchCartData = async () => {
    try {
      const response = await fetch(SummaryApi.getCartItems.url, {
        method: SummaryApi.getCartItems.method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': SummaryApi.bearerToken
        },
        body: JSON.stringify({ customer_id: customerId })
      });

      const data = await response.json();
      if (data.status && Array.isArray(data.data)) {
        const transformedData = data.data.map(item => {
          const product = item.product_details || {};
          let imageUrl = "https://via.placeholder.com/150";
          if (product.product_img1) {
              const domain = SummaryApi.backendDomain.replace(/\/$/, "");
              const imgPath = product.product_img1.replace(/^\//, "");
              imageUrl = `${domain}/product/${imgPath}`;
          }

          return {
            id: item.cart_id,
            productId: product.product_id, 
            name: product.product_name,
            price: Number(product.after_discount_price) || 0,
            quantity: parseInt(item.quantity) || 1,
            image: imageUrl,
            subTitle: product.sub_title
          };
        });
        setCartItems(transformedData);
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  };

  const fetchCustomerDetails = async () => {
    try {
      const response = await fetch(SummaryApi.customerDetailsById.url, {
        method: SummaryApi.customerDetailsById.method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': SummaryApi.bearerToken
        },
        body: JSON.stringify({ customer_id: customerId })
      });
      const data = await response.json();
      if (data.status && data.data) {
        const details = Array.isArray(data.data) ? data.data[0] : data.data;
        setFormData(prev => ({
          ...prev,
          customer_name: details.full_name || details.customer_name || prev.customer_name,
          customer_mobile: details.mobile_number || details.customer_mobile || prev.customer_mobile,
          email: details.email || prev.email,
        }));
      }
    } catch (error) { console.error(error); }
  };

  const fetchUserAddress = async () => {
    try {
      const response = await fetch(SummaryApi.getAddressById.url, {
        method: SummaryApi.getAddressById.method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': SummaryApi.bearerToken
        },
        body: JSON.stringify({ customer_id: customerId })
      });
      const data = await response.json();
      if (data.status && data.data && data.data.length > 0) {
        const addr = data.data[0]; 
        setFormData(prev => ({
          ...prev,
          flat_number: addr.flat_number || "",
          street_name: addr.street_name || "",
          land_mark: addr.land_mark || "",
          city: addr.city || "",
          pincode: addr.pincode || "",
          state: addr.state || ""
        }));
      }
    } catch (error) { console.error(error); }
  };

  const saveAddressToDb = async () => {
    const fullAddressString = `${formData.flat_number}, ${formData.street_name}, ${formData.land_mark}, ${formData.city}, ${formData.state} - ${formData.pincode}`;
    try {
      await fetch(SummaryApi.createAddress.url, {
        method: SummaryApi.createAddress.method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': SummaryApi.bearerToken
        },
        body: JSON.stringify({
          customer_id: customerId,
          customer_name: formData.customer_name,
          customer_mobile: formData.customer_mobile,
          flat_number: formData.flat_number,
          street_name: formData.street_name,
          land_mark: formData.land_mark,
          city: formData.city,
          pincode: formData.pincode,
          state: formData.state,
          address: fullAddressString,
          latitude: "",
          longitude: ""
        })
      });
      return fullAddressString;
    } catch (error) { return fullAddressString; }
  };

  // --- CALCULATIONS ---
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const deliveryCharges = subtotal > 499 ? 0 : 49; 
  const totalAmount = Math.max(0, subtotal + deliveryCharges - discountAmount);

  const getDeliveryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  };

  // --- HANDLERS ---
  const handleApplyCoupon = () => {
    if (!couponCode.trim()) {
      Swal.fire('Error', 'Please enter a coupon code', 'info');
      return;
    }
    if (couponCode.toUpperCase() === "SAVE100") {
      setDiscountAmount(100);
      setIsCouponApplied(true);
      Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 }).fire({ icon: 'success', title: 'Coupon Applied Successfully!' });
    } else {
      Swal.fire('Invalid Coupon', 'This coupon code is not valid.', 'error');
      setDiscountAmount(0);
      setIsCouponApplied(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setDiscountAmount(0);
    setIsCouponApplied(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "pincode") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 6) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
      }
      return; 
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePlaceOrder = async (e) => {
    if (e) e.preventDefault();

    if (!formData.customer_name || !formData.customer_mobile || !formData.flat_number || !formData.street_name || !formData.pincode || !formData.city || !formData.state) {
      Swal.fire('Missing Details', 'Please fill in all mandatory fields.', 'warning');
      return;
    }

    const pincodeRegex = /^\d{6}$/;
    if (!pincodeRegex.test(formData.pincode)) {
        Swal.fire('Invalid Pincode', 'Pincode must be exactly 6 digits.', 'warning');
        return;
    }

    const result = await Swal.fire({
        title: 'Confirm Order',
        text: `Total Amount: ₹${totalAmount.toFixed(2)}`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Place Order',
        confirmButtonColor: '#111827',
        cancelButtonColor: '#d33'
    });

    if (result.isConfirmed) {
        Swal.fire({ title: 'Processing...', allowOutsideClick: false, didOpen: () => { Swal.showLoading(); } });

        try {
            const finalAddressString = await saveAddressToDb();
            const formattedCartItems = cartItems.map(item => ({
                product_id: item.productId,
                quantity: item.quantity
            }));

            const orderPayload = {
              customer_id: customerId,
              customer_name: formData.customer_name,
              mobile_number: formData.customer_mobile, 
              order_address: finalAddressString,      
              state: formData.state,
              order_amount: subtotal.toString(),
              delivery_charges: deliveryCharges.toString(),
              GST: "0", other_charges: "0",
              total_amount: totalAmount.toString(),
              latitude: "", longitude: "",
              delivery_date: getDeliveryDate(),
              payment_mode: formData.paymentMethod.toUpperCase(), 
              cartItems: formattedCartItems 
            };

            const response = await fetch(SummaryApi.order.create.url, {
              method: SummaryApi.order.create.method,
              headers: {
                'Content-Type': 'application/json',
                'Authorization': SummaryApi.bearerToken
              },
              body: JSON.stringify(orderPayload)
            });

            const data = await response.json();

            if (data.status) {
              Swal.fire({
                  icon: 'success',
                  title: 'Order Placed Successfully!',
                  text: `Your Order ID is ${data.order_id || 'generated'}.`,
                  confirmButtonText: 'Go to Home',
                  confirmButtonColor: '#111827'
              }).then(() => { navigate('/'); });
            } else {
              throw new Error(data.message || "Failed to create order");
            }
        } catch (error) {
            Swal.fire('Order Failed', 'Something went wrong. Please try again.', 'error');
        }
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><FaSpinner className="animate-spin text-4xl text-blue-600"/></div>;
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-24 lg:pb-12">
      
      {/* Checkout Progress Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
           <Link to="/cart" className="flex items-center text-gray-500 hover:text-gray-900 transition font-medium">
             <FaArrowLeft className="mr-2" /> Back to Cart
           </Link>
           <div className="hidden sm:flex items-center gap-4 text-sm font-medium">
              <span className="text-green-600 flex items-center"><FaCheckCircle className="mr-1"/> Cart</span>
              <span className="w-8 h-px bg-gray-300"></span>
              <span className="text-blue-600 flex items-center"><div className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs mr-1">2</div> Checkout</span>
              <span className="w-8 h-px bg-gray-300"></span>
              <span className="text-gray-400">Done</span>
           </div>
           <div className="flex items-center text-gray-500 text-sm">
             <FaShieldAlt className="text-green-500 mr-1.5"/> Secure Checkout
           </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* LEFT COLUMN: Input Forms */}
          <div className="lg:w-2/3 space-y-6">
            
            {/* 1. Contact Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                 <div className="p-2 bg-blue-100 text-blue-600 rounded-full"><FaUser size={14} /></div>
                 <h2 className="text-lg font-bold text-gray-800">Contact Details</h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                <InputGroup label="Full Name" name="customer_name" value={formData.customer_name} onChange={handleInputChange} placeholder="John Doe" required />
                <InputGroup label="Mobile Number" name="customer_mobile" value={formData.customer_mobile} onChange={handleInputChange} placeholder="9876543210" required readOnly={true} />
                <InputGroup label="Email Address" name="email" value={formData.email} onChange={handleInputChange} placeholder="john@example.com" className="md:col-span-2" />
              </div>
            </div>

            {/* 2. Shipping Address */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                 <div className="p-2 bg-blue-100 text-blue-600 rounded-full"><FaMapMarkerAlt size={14} /></div>
                 <h2 className="text-lg font-bold text-gray-800">Shipping Address</h2>
              </div>
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                   <InputGroup label="Flat / House No." name="flat_number" value={formData.flat_number} onChange={handleInputChange} placeholder="Flat / House No." required />
                   <InputGroup label="Landmark (Optional)" name="land_mark" value={formData.land_mark} onChange={handleInputChange} placeholder="Landmark (Optional)" />
                </div>
                <InputGroup label="Street / Area / Colony" name="street_name" value={formData.street_name} onChange={handleInputChange} placeholder="Street / Area / Colony" required />
                <div className="grid grid-cols-3 gap-3 md:gap-5">
                  <InputGroup label="City" name="city" value={formData.city} onChange={handleInputChange} placeholder="City" required className="col-span-3 sm:col-span-1" />
                  <InputGroup label="State" name="state" value={formData.state} onChange={handleInputChange} placeholder="State" required className="col-span-3 sm:col-span-1" />
                  <InputGroup label="Pincode" name="pincode" value={formData.pincode} onChange={handleInputChange} placeholder="123456" required className="col-span-3 sm:col-span-1" />
                </div>
              </div>
            </div>

            {/* 3. Payment Method */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                 <div className="p-2 bg-blue-100 text-blue-600 rounded-full"><FaCreditCard size={14} /></div>
                 <h2 className="text-lg font-bold text-gray-800">Payment Method</h2>
              </div>
              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className={`relative flex flex-col p-4 border rounded-xl cursor-pointer transition-all duration-200 ${formData.paymentMethod === 'cod' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-gray-900">Cash on Delivery</span>
                    {formData.paymentMethod === 'cod' && <FaCheckCircle className="text-blue-600" />}
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Pay with cash when your order arrives.</p>
                  <div className="flex items-center gap-2 text-green-600 text-sm font-medium mt-auto">
                    <FaMoneyBillWave /> Cash
                  </div>
                  <input type="radio" name="paymentMethod" value="cod" checked={formData.paymentMethod === 'cod'} onChange={handleInputChange} className="hidden" />
                </label>

                <label className={`relative flex flex-col p-4 border rounded-xl cursor-pointer transition-all duration-200 ${formData.paymentMethod === 'online' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
                   <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-gray-900">Pay Online</span>
                    {formData.paymentMethod === 'online' && <FaCheckCircle className="text-blue-600" />}
                  </div>
                  <p className="text-xs text-gray-500 mb-3">UPI, Credit/Debit Cards, Net Banking.</p>
                  <div className="flex items-center gap-2 text-blue-600 text-sm font-medium mt-auto">
                    <FaCreditCard /> Secured
                  </div>
                  <input type="radio" name="paymentMethod" value="online" checked={formData.paymentMethod === 'online'} onChange={handleInputChange} className="hidden" />
                </label>
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN: Order Summary */}
          <div className="lg:w-1/3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 lg:sticky lg:top-24">
              <div className="p-6 border-b border-gray-100">
                  <h2 className="text-lg font-bold text-gray-900">Order Summary</h2>
                  <p className="text-sm text-gray-500 mt-1">{cartItems.length} Items in cart</p>
              </div>
              
              <div className="p-6 max-h-[250px] overflow-y-auto custom-scrollbar space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex gap-4">
                    <div className="w-14 h-14 bg-gray-50 rounded-lg overflow-hidden border border-gray-100 flex-shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-contain mix-blend-multiply" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 truncate">{item.name}</h4>
                      <p className="text-xs text-gray-500 truncate">{item.subTitle}</p>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-gray-500">Qty: {item.quantity}</span>
                        <span className="text-sm font-medium text-gray-900">₹{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* COUPON SECTION */}
              <div className="px-6 py-4 bg-gray-50 border-t border-b border-gray-100">
                  {!isCouponApplied ? (
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaTag className="text-gray-400" />
                            </div>
                            <input 
                                type="text"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                placeholder="Coupon Code"
                                className="pl-9 w-full rounded-lg border-gray-300 border py-2 text-sm focus:ring-1 focus:ring-blue-500 outline-none uppercase"
                            />
                        </div>
                        <button onClick={handleApplyCoupon} className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition">Apply</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                            <FaTag className="text-green-600" />
                            <div>
                                <p className="text-sm font-bold text-green-800 tracking-wide">{couponCode}</p>
                                <p className="text-xs text-green-600">Coupon Applied</p>
                            </div>
                        </div>
                        <button onClick={handleRemoveCoupon} className="text-gray-400 hover:text-red-500">
                            <FaTimes />
                        </button>
                    </div>
                  )}
              </div>

              {/* TOTALS SECTION */}
              <div className="p-6 bg-white space-y-3">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal</span>
                  <span>₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Shipping Charges</span>
                  <span className={deliveryCharges === 0 ? "text-green-600 font-medium" : ""}>
                    {deliveryCharges === 0 ? "Free" : `₹${deliveryCharges}`}
                  </span>
                </div>
                {isCouponApplied && (
                    <div className="flex justify-between text-sm text-green-600 font-medium">
                        <span>Discount ({couponCode})</span>
                        <span>- ₹{discountAmount.toFixed(2)}</span>
                    </div>
                )}
                <div className="border-t border-gray-200 pt-3 mt-1 flex justify-between items-center">
                  <span className="text-base font-bold text-gray-900">Total To Pay</span>
                  <span className="text-xl font-bold text-gray-900">₹{totalAmount.toFixed(2)}</span>
                </div>
                <button onClick={handlePlaceOrder} className="w-full mt-4 bg-gray-900 text-white font-bold py-4 rounded-xl hover:bg-gray-800 transition duration-300 shadow-lg flex items-center justify-center gap-2 group">
                    <FaLock className="w-3 h-3 text-gray-400 group-hover:text-white transition-colors" /> 
                    Confirm Order
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

       {/* Mobile Sticky Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 lg:hidden shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-40">
        <div className="flex gap-4 items-center max-w-7xl mx-auto">
           <div className="flex-1">
               <p className="text-xs text-gray-500 uppercase font-semibold">Total Amount</p>
               <h3 className="text-lg font-bold text-gray-900">₹{totalAmount.toFixed(2)}</h3>
           </div>
           <button onClick={handlePlaceOrder} className="bg-gray-900 text-white px-6 py-3 rounded-lg font-bold text-sm hover:bg-gray-800 flex items-center gap-2">
               Place Order <FaLock size={12}/>
           </button>
        </div>
      </div>

    </div>
  );
};

export default Checkout;